import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../../config/api';
import { getHeaders } from '../../utils/api';
import './FcmManagement.css';

type Frequency = 'LOW' | 'MEDIUM' | 'HIGH';
type AutomationCategory = 'local' | 'national';

interface FrequencyConfig {
  national: Record<Frequency, number>;
  local: Record<Frequency, number>;
  nationalHourly: Record<Frequency, Record<string, number>>;
  localHourly: Record<Frequency, Record<string, number>>;
  deliveryStartHour: number;
  deliveryEndHour: number;
  nationalDeliveryMinute?: number;
  localDeliveryMinute?: number;
  timeZone: string;
}

interface AutomationRunResult {
  regionalActiveRegionCount: number;
  regionalApiSuccesses: number;
  regionalApiFailures: number;
  regionalProcessingFailures: number;
  regionalReceivedArticles: number;
  regionalInsertedArticles: number;
  regionalUpdatedExistingArticles: number;
  regionalSkippedArticles: number;
  localTargetCount: number;
  localRegionCount: number;
  localCandidateCount: number;
  localSelectedCount: number;
  nationalCandidateCount: number;
  nationalSelectedCount: number;
  nationalTargetCount: number;
  channelAttempts: number;
  channelSuccesses: number;
  membershipAttempts: number;
  membershipSuccesses: number;
  membershipFailures: number;
  invalidTokensDeactivated: number;
  channelTopics: string[];
  capsBypassed: boolean;
  messages: string[];
}

const LEGACY_RUN_HOURS = new Set([7, 11, 16]);
const CMS_HOURLY_DEFAULTS_STORAGE_KEY = 'notificationHourlyDefaultsV1Applied';

const applyCmsHourlyDefaults = (config: FrequencyConfig): FrequencyConfig => {
  if (window.localStorage.getItem(CMS_HOURLY_DEFAULTS_STORAGE_KEY) === 'true') return config;
  const hasConfiguredNonLegacyHour = [config.nationalHourly, config.localHourly].some(hourly =>
    (['LOW', 'MEDIUM', 'HIGH'] as Frequency[]).some(frequency =>
      Object.entries(hourly[frequency]).some(([hour, value]) => !LEGACY_RUN_HOURS.has(Number(hour)) && value > 0)
    )
  );
  if (hasConfiguredNonLegacyHour) return config;

  const hours = Array.from(
    { length: config.deliveryEndHour - config.deliveryStartHour + 1 },
    (_, index) => config.deliveryStartHour + index
  );
  const withDefaults = (hourly: Record<Frequency, Record<string, number>>) =>
    (['LOW', 'MEDIUM', 'HIGH'] as Frequency[]).reduce((result, frequency) => {
      result[frequency] = hours.reduce<Record<string, number>>((values, hour) => {
        values[hour] = hourly[frequency][hour] === 0 ? 1 : (hourly[frequency][hour] ?? 1);
        return values;
      }, {});
      return result;
    }, {} as Record<Frequency, Record<string, number>>);
  const nationalHourly = withDefaults(config.nationalHourly);
  const localHourly = withDefaults(config.localHourly);
  const totals = (hourly: Record<Frequency, Record<string, number>>) =>
    (['LOW', 'MEDIUM', 'HIGH'] as Frequency[]).reduce((result, frequency) => {
      result[frequency] = Object.values(hourly[frequency]).reduce((sum, value) => sum + value, 0);
      return result;
    }, {} as Record<Frequency, number>);

  return { ...config, national: totals(nationalHourly), local: totals(localHourly), nationalHourly, localHourly };
};

const SettingsManagement = () => {
  const [frequencyConfig, setFrequencyConfig] = useState<FrequencyConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [runningAutomation, setRunningAutomation] = useState<AutomationCategory | null>(null);
  const [lastAutomationCategory, setLastAutomationCategory] = useState<AutomationCategory | null>(null);
  const [automationResult, setAutomationResult] = useState<AutomationRunResult | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FCM.FREQUENCY_CONFIG}`,
          { headers: getHeaders() }
        );
        setFrequencyConfig(applyCmsHourlyDefaults(response.data));
      } catch (err) {
        setError('Failed to load notification frequency configuration');
      }
    };
    loadConfig();
  }, []);

  const updateHourlyCap = (category: 'national' | 'local', frequency: Frequency, hour: number, value: string) => {
    const cap = Math.max(0, Number.parseInt(value || '0', 10));
    const hourlyKey = category === 'national' ? 'nationalHourly' : 'localHourly';
    setFrequencyConfig(current => {
      if (!current) return current;
      const hourly = current[hourlyKey];
      const nextLow = { ...hourly.LOW };
      const nextMedium = { ...hourly.MEDIUM };
      const nextHigh = { ...hourly.HIGH };
      if (frequency === 'LOW') {
        nextLow[hour] = cap;
        nextMedium[hour] = Math.max(nextMedium[hour] ?? 0, cap);
        nextHigh[hour] = Math.max(nextHigh[hour] ?? 0, nextMedium[hour]);
      } else if (frequency === 'MEDIUM') {
        nextMedium[hour] = cap;
        nextLow[hour] = Math.min(nextLow[hour] ?? 0, cap);
        nextHigh[hour] = Math.max(nextHigh[hour] ?? 0, cap);
      } else {
        nextHigh[hour] = cap;
        nextMedium[hour] = Math.min(nextMedium[hour] ?? 0, cap);
        nextLow[hour] = Math.min(nextLow[hour] ?? 0, nextMedium[hour]);
      }
      return { ...current, [hourlyKey]: { LOW: nextLow, MEDIUM: nextMedium, HIGH: nextHigh } };
    });
    setError(null);
    setSuccess(null);
  };

  const saveFrequencyConfig = async () => {
    if (!frequencyConfig) return;
    const hours = Array.from(
      { length: frequencyConfig.deliveryEndHour - frequencyConfig.deliveryStartHour + 1 },
      (_, index) => frequencyConfig.deliveryStartHour + index
    );
    const ordered = (caps: Record<Frequency, Record<string, number>>) =>
      hours.every(hour => caps.LOW[hour] <= caps.MEDIUM[hour] && caps.MEDIUM[hour] <= caps.HIGH[hour]);
    if (!ordered(frequencyConfig.nationalHourly) || !ordered(frequencyConfig.localHourly)) {
      setError('Every hour must satisfy Low ≤ Medium ≤ High');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const response = await axios.put(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FCM.FREQUENCY_CONFIG}`,
        frequencyConfig,
        { headers: getHeaders() }
      );
      setFrequencyConfig(response.data);
      window.localStorage.setItem(CMS_HOURLY_DEFAULTS_STORAGE_KEY, 'true');
      setSuccess('Notification frequency configuration saved');
    } catch (err) {
      const serverMessage = axios.isAxiosError(err) && typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : null;
      setError(serverMessage || 'Failed to save notification frequency configuration');
    } finally {
      setSaving(false);
    }
  };

  const runAutomationNow = async (category: AutomationCategory) => {
    try {
      setRunningAutomation(category);
      setAutomationResult(null);
      setLastAutomationCategory(null);
      setError(null);
      setSuccess(null);
      const endpoint = category === 'local'
        ? API_CONFIG.ENDPOINTS.FCM.RUN_LOCAL_AUTOMATION_NOW
        : API_CONFIG.ENDPOINTS.FCM.RUN_NATIONAL_AUTOMATION_NOW;
      const response = await axios.post(`${API_CONFIG.BASE_URL}${endpoint}`, {}, { headers: getHeaders() });
      setAutomationResult(response.data);
      setLastAutomationCategory(category);
      setSuccess(`${category === 'local' ? 'Local' : 'National'} automation completed: ${response.data.channelSuccesses} successful channel sends`);
    } catch (err) {
      setError(`Failed to run ${category} notification automation`);
    } finally {
      setRunningAutomation(null);
    }
  };

  if (!frequencyConfig) {
    return <div className="fcm-management"><h2>Settings</h2>{error ? <div className="error-message">{error}</div> : <div className="loading">Loading settings…</div>}</div>;
  }

  const nationalMinute = frequencyConfig.nationalDeliveryMinute ?? 0;
  const localMinute = frequencyConfig.localDeliveryMinute ?? 30;

  return (
    <div className="fcm-management settings-management">
      <h2>Settings</h2>
      <section className="frequency-config-card">
        <div>
          <h2>Automated notification frequency</h2>
          <p>
            National: {frequencyConfig.deliveryStartHour.toString().padStart(2, '0')}:{nationalMinute.toString().padStart(2, '0')}–
            {frequencyConfig.deliveryEndHour.toString().padStart(2, '0')}:{nationalMinute.toString().padStart(2, '0')}; local:
            {frequencyConfig.deliveryStartHour.toString().padStart(2, '0')}:{localMinute.toString().padStart(2, '0')}–
            {frequencyConfig.deliveryEndHour.toString().padStart(2, '0')}:{localMinute.toString().padStart(2, '0')} ({frequencyConfig.timeZone})
          </p>
          <p className="frequency-rollover-note">
            Configure the maximum notifications sent by each hourly scheduler run. Limits are independent for national and local channels.
          </p>
        </div>
        <div className="frequency-config-grid">
          {(['national', 'local'] as const).map(category => {
            const hourly = category === 'national' ? frequencyConfig.nationalHourly : frequencyConfig.localHourly;
            const minute = category === 'national' ? nationalMinute : localMinute;
            return (
              <fieldset key={category}>
                <legend>{category === 'national' ? 'National news' : 'Local news'}</legend>
                <div className="hourly-config-table">
                  <div className="hourly-config-header"><span>Run</span><span>Low</span><span>Medium</span><span>High</span></div>
                  {Array.from(
                    { length: frequencyConfig.deliveryEndHour - frequencyConfig.deliveryStartHour + 1 },
                    (_, index) => frequencyConfig.deliveryStartHour + index
                  ).map(hour => (
                    <div className="hourly-config-row" key={hour}>
                      <span>{hour.toString().padStart(2, '0')}:{minute.toString().padStart(2, '0')}</span>
                      {(['LOW', 'MEDIUM', 'HIGH'] as Frequency[]).map(frequency => (
                        <input key={frequency} type="number" min="0" value={hourly[frequency][hour] ?? 1}
                          aria-label={`${category} ${frequency.toLowerCase()} ${hour}:${minute.toString().padStart(2, '0')}`}
                          onChange={event => updateHourlyCap(category, frequency, hour, event.target.value)} />
                      ))}
                    </div>
                  ))}
                  <div className="hourly-config-total">
                    <span>Daily total</span>
                    {(['LOW', 'MEDIUM', 'HIGH'] as Frequency[]).map(frequency => (
                      <span key={frequency}>{Object.values(hourly[frequency]).reduce((sum, value) => sum + value, 0)}</span>
                    ))}
                  </div>
                </div>
              </fieldset>
            );
          })}
        </div>
        <div className="frequency-config-actions">
          <button type="button" onClick={saveFrequencyConfig} disabled={saving || runningAutomation !== null}>
            {saving ? 'Saving…' : 'Save frequency limits'}
          </button>
          <button type="button" className="run-automation-button" onClick={() => runAutomationNow('local')}
            disabled={runningAutomation !== null || saving}>
            {runningAutomation === 'local' ? 'Running local notification…' : 'Run local notification (bypass caps)'}
          </button>
          <button type="button" className="run-automation-button" onClick={() => runAutomationNow('national')}
            disabled={runningAutomation !== null || saving}>
            {runningAutomation === 'national' ? 'Running national notification…' : 'Run national notification (bypass caps)'}
          </button>
        </div>
        {error && <div className="error-message" role="alert">{error}</div>}
        {success && <div className="success-message" role="status">{success}</div>}
        {automationResult && (
          <div className="automation-run-result">
            <strong>Latest immediate {lastAutomationCategory} run</strong>
            <span>Channel sends: {automationResult.channelSuccesses}/{automationResult.channelAttempts}</span>
            <span>Channel memberships: {automationResult.membershipSuccesses}/{automationResult.membershipAttempts} confirmed; {automationResult.membershipFailures} failed; {automationResult.invalidTokensDeactivated} invalid tokens deactivated</span>
            <span>Target topics: {automationResult.channelTopics.length ? automationResult.channelTopics.join(', ') : 'none'}</span>
            <span>Automated caps bypassed: {automationResult.capsBypassed ? 'Yes (these sends do not consume the automated allowance)' : 'No'}</span>
            {lastAutomationCategory === 'local' && <>
              <span>Regional fetch: {automationResult.regionalActiveRegionCount} active regions; {automationResult.regionalApiSuccesses} API successes / {automationResult.regionalApiFailures} failures</span>
              <span>Regional content: {automationResult.regionalReceivedArticles} received; {automationResult.regionalInsertedArticles} inserted; {automationResult.regionalUpdatedExistingArticles} updated; {automationResult.regionalSkippedArticles} skipped; {automationResult.regionalProcessingFailures} processing failures</span>
              <span>Local: {automationResult.localSelectedCount} selected from {automationResult.localCandidateCount}</span>
              <span>Local targets/regions: {automationResult.localTargetCount}/{automationResult.localRegionCount}</span>
            </>}
            {lastAutomationCategory === 'national' &&
              <span>National: {automationResult.nationalSelectedCount} selected from {automationResult.nationalCandidateCount}; {automationResult.nationalTargetCount} configured channels</span>}
            {automationResult.messages.length > 0 && <ul>{automationResult.messages.map((message, index) => <li key={index}>{message}</li>)}</ul>}
          </div>
        )}
      </section>
    </div>
  );
};

export default SettingsManagement;
